'use client';

import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, FileVideo, CheckCircle, Package, AlertCircle, Archive, Film } from 'lucide-react';

interface VideoDropZoneProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  /** Scene Pack mode: exactly 1 video file allowed */
  scenepackMode?: boolean;
  /** Bundle mode: multiple video files OR one ZIP */
  bundleMode?: boolean;
  onFilesSelect?: (files: File[]) => void;
  selectedFiles?: File[];
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm', 'video/avi', 'video/mov'];
const ACCEPTED_ZIP_TYPES = ['application/zip', 'application/x-zip-compressed', 'application/x-zip'];
const ACCEPTED_ZIP_EXTS = ['.zip'];

function isVideoFile(file: File): boolean {
  return ACCEPTED_VIDEO_TYPES.includes(file.type) || file.type.startsWith('video/');
}

function isZipFile(file: File): boolean {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ACCEPTED_ZIP_TYPES.includes(file.type) || ACCEPTED_ZIP_EXTS.includes(ext);
}

export default function VideoDropZone({
  onFileSelect,
  selectedFile,
  scenepackMode = false,
  bundleMode = false,
  onFilesSelect,
  selectedFiles = [],
}: VideoDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─── Scene Pack Mode ───────────────────────────────────────────────────────
  const handleScenepackDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 1) {
        setError('Scene Packs allow 1 file only. Please drop a single video file.');
        return;
      }
      const file = files[0];
      if (!file) return;
      if (!isVideoFile(file)) {
        setError('Unsupported file type. Please upload a video file (MP4, MOV, AVI, MKV, WebM).');
        return;
      }
      setError(null);
      onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleScenepackInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 1) {
      setError('Scene Packs allow 1 file only. Please select a single video file.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const file = files[0];
    if (!file) return;
    if (!isVideoFile(file)) {
      setError('Unsupported file type. Please upload a video file (MP4, MOV, AVI, MKV, WebM).');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setError(null);
    onFileSelect(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ─── Bundle Mode ───────────────────────────────────────────────────────────
  const handleBundleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      // Check if dropping a ZIP
      if (files.length === 1 && isZipFile(files[0])) {
        // If there are already individual clips, warn
        if (selectedFiles.length > 0 && !isZipFile(selectedFiles[0])) {
          setError('You already have individual clips selected. Remove them first to upload a ZIP archive.');
          return;
        }
        setError(null);
        onFilesSelect && onFilesSelect([files[0]]);
        return;
      }

      // Check if already has a ZIP
      if (selectedFiles.length > 0 && isZipFile(selectedFiles[0])) {
        setError('You already have a ZIP archive selected. Remove it first to add individual clips.');
        return;
      }

      const videoFiles = files.filter(isVideoFile);
      const invalidFiles = files.filter((f) => !isVideoFile(f));
      if (invalidFiles.length > 0) {
        setError(`${invalidFiles.length} unsupported file(s) skipped. Only video files (MP4, MOV, AVI, MKV, WebM) are accepted.`);
      } else {
        setError(null);
      }
      if (videoFiles.length > 0 && onFilesSelect) {
        onFilesSelect([...selectedFiles, ...videoFiles]);
      }
    },
    [onFilesSelect, selectedFiles]
  );

  const handleBundleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if selecting a ZIP
    if (files.length === 1 && isZipFile(files[0])) {
      if (selectedFiles.length > 0 && !isZipFile(selectedFiles[0])) {
        setError('You already have individual clips selected. Remove them first to upload a ZIP archive.');
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
      setError(null);
      onFilesSelect && onFilesSelect([files[0]]);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    // Check if already has a ZIP
    if (selectedFiles.length > 0 && isZipFile(selectedFiles[0])) {
      setError('You already have a ZIP archive selected. Remove it first to add individual clips.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    const videoFiles = files.filter(isVideoFile);
    const invalidFiles = files.filter((f) => !isVideoFile(f) && !isZipFile(f));
    if (invalidFiles.length > 0) {
      setError(`${invalidFiles.length} unsupported file(s) skipped. Only video files (MP4, MOV, AVI, MKV, WebM) or a single ZIP are accepted.`);
    } else {
      setError(null);
    }
    if (videoFiles.length > 0 && onFilesSelect) {
      onFilesSelect([...selectedFiles, ...videoFiles]);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    if (onFilesSelect) {
      const updated = selectedFiles.filter((_, i) => i !== index);
      onFilesSelect(updated);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);

  // ─── Default single mode handlers ─────────────────────────────────────────
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && isVideoFile(file)) onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  // ─── Error Banner ──────────────────────────────────────────────────────────
  const ErrorBanner = () =>
    error ? (
      <div
        className="flex items-start gap-2 p-3 rounded-xl text-xs mt-3"
        style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: 'var(--danger)' }}
      >
        <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
        <span>{error}</span>
        <button
          type="button"
          onClick={() => setError(null)}
          className="ml-auto flex-shrink-0 opacity-70 hover:opacity-100"
        >
          <X size={12} />
        </button>
      </div>
    ) : null;

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE PACK MODE — exactly 1 video file
  // ═══════════════════════════════════════════════════════════════════════════
  if (scenepackMode) {
    return (
      <div className="space-y-3">
        {/* Rule banner */}
        <div
          className="flex items-center gap-2 p-3 rounded-xl text-xs font-medium"
          style={{ background: 'rgba(108, 99, 255, 0.08)', border: '1px solid rgba(108, 99, 255, 0.25)', color: 'rgba(108, 99, 255, 0.95)' }}
        >
          <Film size={13} className="flex-shrink-0" />
          <span>Scene Packs allow <strong>1 file only</strong> — upload a single video file for this pack.</span>
        </div>

        {selectedFile ? (
          <div
            className="rounded-xl p-4 flex items-center gap-4"
            style={{ background: 'rgba(34, 197, 94, 0.06)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
          >
            <span
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(34, 197, 94, 0.15)' }}
            >
              <FileVideo size={22} style={{ color: 'var(--success)' }} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <CheckCircle size={14} style={{ color: 'var(--success)' }} />
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                  {selectedFile.name}
                </p>
              </div>
              <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                {formatBytes(selectedFile.size)} · {selectedFile.type.split('/')[1]?.toUpperCase()}
              </p>
            </div>
            <button
              type="button"
              onClick={() => { onFileSelect(null); setError(null); }}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-all hover:opacity-80 flex-shrink-0"
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)' }}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div
            onDrop={handleScenepackDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => inputRef.current?.click()}
            className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-10 px-6 cursor-pointer transition-all duration-200"
            style={{
              borderColor: isDragging ? 'var(--primary)' : 'var(--border)',
              background: isDragging ? 'rgba(108, 99, 255, 0.06)' : 'var(--muted)',
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleScenepackInput}
            />
            <span
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-200"
              style={{
                background: isDragging ? 'rgba(108, 99, 255, 0.2)' : 'rgba(108, 99, 255, 0.1)',
                border: '1px solid rgba(108, 99, 255, 0.3)',
              }}
            >
              <Upload size={24} style={{ color: 'var(--primary)' }} />
            </span>
            <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>
              {isDragging ? 'Drop your video here' : 'Drag & drop your video'}
            </p>
            <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
              or click to browse · MP4, MOV, AVI, MKV, WebM · 1 file only
            </p>
          </div>
        )}

        <ErrorBanner />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BUNDLE MODE — multiple video files OR one ZIP
  // ═══════════════════════════════════════════════════════════════════════════
  if (bundleMode) {
    const hasZip = selectedFiles.length === 1 && isZipFile(selectedFiles[0]);
    const hasClips = selectedFiles.length > 0 && !hasZip;

    return (
      <div className="space-y-3">
        {/* Rule banner */}
        <div
          className="flex items-start gap-2 p-3 rounded-xl text-xs font-medium"
          style={{ background: 'rgba(59, 158, 255, 0.08)', border: '1px solid rgba(59, 158, 255, 0.25)', color: 'rgba(59, 158, 255, 0.95)' }}
        >
          <Package size={13} className="flex-shrink-0 mt-0.5" />
          <span>
            Clip Bundles support <strong>multiple individual video files</strong> or <strong>one ZIP archive</strong> containing all clips.
          </span>
        </div>

        {/* Drop zone — only show if no ZIP selected */}
        {!hasZip && (
          <div
            onDrop={handleBundleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => inputRef.current?.click()}
            className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-8 px-6 cursor-pointer transition-all duration-200"
            style={{
              borderColor: isDragging ? 'var(--primary)' : 'var(--border)',
              background: isDragging ? 'rgba(108, 99, 255, 0.06)' : 'var(--muted)',
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="video/*,.zip"
              multiple
              className="hidden"
              onChange={handleBundleInput}
            />
            <span
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-all duration-200"
              style={{
                background: isDragging ? 'rgba(108, 99, 255, 0.2)' : 'rgba(108, 99, 255, 0.1)',
                border: '1px solid rgba(108, 99, 255, 0.3)',
              }}
            >
              <Package size={24} style={{ color: 'var(--primary)' }} />
            </span>
            <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>
              {isDragging ? 'Drop your clips or ZIP here' : 'Drag & drop clips or a ZIP archive'}
            </p>
            <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
              or click to browse · Multiple MP4/MOV/AVI/MKV/WebM <em>or</em> one ZIP
            </p>
          </div>
        )}

        {/* ZIP selected */}
        {hasZip && (
          <div
            className="rounded-xl p-4 flex items-center gap-4"
            style={{ background: 'rgba(34, 197, 94, 0.06)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
          >
            <span
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(34, 197, 94, 0.15)' }}
            >
              <Archive size={22} style={{ color: 'var(--success)' }} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <CheckCircle size={14} style={{ color: 'var(--success)' }} />
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                  {selectedFiles[0].name}
                </p>
              </div>
              <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                {formatBytes(selectedFiles[0].size)} · ZIP Archive
              </p>
            </div>
            <button
              type="button"
              onClick={() => { onFilesSelect && onFilesSelect([]); setError(null); }}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-all hover:opacity-80 flex-shrink-0"
              style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Individual clips list */}
        {hasClips && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>
                {selectedFiles.length} clip{selectedFiles.length !== 1 ? 's' : ''} selected
              </p>
              <button
                type="button"
                onClick={() => { onFilesSelect && onFilesSelect([]); setError(null); }}
                className="text-xs font-medium transition-opacity hover:opacity-70"
                style={{ color: 'var(--danger)' }}
              >
                Remove all
              </button>
            </div>
            {selectedFiles.map((file, index) => (
              <div
                key={`bundle-file-${index}`}
                className="rounded-xl p-3 flex items-center gap-3"
                style={{ background: 'rgba(34, 197, 94, 0.06)', border: '1px solid rgba(34, 197, 94, 0.25)' }}
              >
                <span
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(34, 197, 94, 0.15)' }}
                >
                  <FileVideo size={16} style={{ color: 'var(--success)' }} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <CheckCircle size={12} style={{ color: 'var(--success)' }} />
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                      {file.name}
                    </p>
                  </div>
                  <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
                    {formatBytes(file.size)} · {file.type.split('/')[1]?.toUpperCase()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg transition-all hover:opacity-80 flex-shrink-0"
                  style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)' }}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <ErrorBanner />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DEFAULT SINGLE MODE
  // ═══════════════════════════════════════════════════════════════════════════
  if (selectedFile) {
    return (
      <div
        className="rounded-xl p-4 flex items-center gap-4"
        style={{ background: 'rgba(34, 197, 94, 0.06)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
      >
        <span
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(34, 197, 94, 0.15)' }}
        >
          <FileVideo size={22} style={{ color: 'var(--success)' }} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <CheckCircle size={14} style={{ color: 'var(--success)' }} />
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
              {selectedFile.name}
            </p>
          </div>
          <p className="text-xs font-mono" style={{ color: 'var(--muted-foreground)' }}>
            {formatBytes(selectedFile.size)} · {selectedFile.type.split('/')[1]?.toUpperCase()}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onFileSelect(null)}
          className="w-8 h-8 flex items-center justify-center rounded-lg transition-all hover:opacity-80 flex-shrink-0"
          style={{ background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)' }}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => inputRef.current?.click()}
      className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-10 px-6 cursor-pointer transition-all duration-200"
      style={{
        borderColor: isDragging ? 'var(--primary)' : 'var(--border)',
        background: isDragging ? 'rgba(108, 99, 255, 0.06)' : 'var(--muted)',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={handleFileInput}
      />
      <span
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-200"
        style={{
          background: isDragging ? 'rgba(108, 99, 255, 0.2)' : 'rgba(108, 99, 255, 0.1)',
          border: '1px solid rgba(108, 99, 255, 0.3)',
        }}
      >
        <Upload size={24} style={{ color: 'var(--primary)' }} />
      </span>
      <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>
        {isDragging ? 'Drop your video here' : 'Drag & drop your video'}
      </p>
      <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
        or click to browse · MP4, MOV, AVI, MKV, WebM
      </p>
    </div>
  );
}